import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { API_URL, getHeaders, Spinner } from './shared';
import logger from '../../utils/logger';

export function RoleManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/roles/users`, { headers: getHeaders() }),
        fetch(`${API_URL}/api/admin/roles`, { headers: getHeaders() }),
      ]);
      if (uRes.ok) setUsers((await uRes.json()).users || []);
      if (rRes.ok) setRoles((await rRes.json()).roles || []);
    } catch (err) { logger.error(err); toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  const createRole = async () => {
    if (!newName.trim()) { toast.error('Name required'); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/roles`, { method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ name: newName.trim().toLowerCase().replace(/\s+/g, '_'), description: newDesc }) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success('Role created');
      setShowNew(false); setNewName(''); setNewDesc(''); fetch_();
    } catch (e) { toast.error(e.message); }
  };

  const assignRole = async (userId) => {
    if (!selectedRole) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/roles/assign`, { method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ user_id: userId, role: selectedRole }) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success('Role updated');
      setEditingUser(null); setSelectedRole(''); fetch_();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <Spinner text="Loading roles..." />;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wider">Available Roles</h3>
          <button data-testid="create-role-btn" onClick={() => setShowNew(!showNew)}
            className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition font-medium">+ New Role</button>
        </div>
        {showNew && (
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-4 space-y-3">
            <input data-testid="new-role-name" type="text" placeholder="Role name (e.g., analyst)" value={newName} onChange={e => setNewName(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-green-500" />
            <input data-testid="new-role-description" type="text" placeholder="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-green-500" />
            <div className="flex gap-2">
              <button data-testid="save-role-btn" onClick={createRole} className="text-xs bg-green-600 text-white px-4 py-1.5 rounded-lg hover:bg-green-700 font-medium">Create</button>
              <button onClick={() => setShowNew(false)} className="text-xs text-gray-400 hover:text-gray-300">Cancel</button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {roles.map(r => (
            <div key={r.name} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-300">
              <span className="font-medium text-white">{r.name}</span>
              {r.description && <span className="text-gray-500 ml-1">- {r.description}</span>}
              {r.is_system && <span className="text-blue-400 ml-1">(system)</span>}
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wider mb-3">User Roles</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-700">
              <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr></thead>
            <tbody>{users.map(u => (
              <tr key={u.user_id} className="border-b border-gray-800 hover:bg-gray-800/50 transition">
                <td className="py-2.5 px-3 text-white">{u.name}</td>
                <td className="py-2.5 px-3 text-gray-400 text-xs">{u.email}</td>
                <td className="py-2.5 px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${
                  (() => {
                    if (u.role === 'owner') return 'bg-purple-600/20 text-purple-400';
                    if (u.role === 'admin') return 'bg-blue-600/20 text-blue-400';
                    if (u.role === 'engineer') return 'bg-teal-600/20 text-teal-400';
                    return 'bg-gray-700 text-gray-400';
                  })()
                }`}>{u.role || 'user'}</span></td>
                <td className="py-2.5 px-3">
                  {editingUser === u.user_id ? (
                    <div className="flex items-center gap-2">
                      <select data-testid={`role-select-${u.user_id}`} value={selectedRole} onChange={e => setSelectedRole(e.target.value)}
                        className="text-xs bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-white">
                        <option value="">Select...</option>
                        {roles.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
                      </select>
                      <button data-testid={`save-role-${u.user_id}`} onClick={() => assignRole(u.user_id)}
                        className="text-xs bg-green-600 text-white px-2 py-1 rounded-lg hover:bg-green-700">Save</button>
                      <button onClick={() => setEditingUser(null)} className="text-xs text-gray-400 hover:text-gray-300">Cancel</button>
                    </div>
                  ) : (
                    <button data-testid={`edit-role-${u.user_id}`} onClick={() => { setEditingUser(u.user_id); setSelectedRole(u.role || 'user'); }}
                      className="text-xs text-blue-400 hover:text-blue-300 font-medium">Change</button>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
